/* Utility for fetch.
Please use this instead of axios or any other library for fetching anything from server,
if using a rest API */

import compareAsc from "date-fns/compareAsc";
import addMinutes from "date-fns/addMinutes";
import { CACHE_EXPIRE_IN_MINUTES } from "./constants";
import { CACHE_KEYS } from "./commonHelpers";
import { getUrlParam } from "./commonHelpers";
import { useRouter } from "next/router";

interface fetchCacheUtilProps {
  url: string;
  cacheKey: CACHE_KEYS;
  ctype: string;
  useToken: boolean;
  useHeaders?: boolean;
  token?: string;
}

const fetchSSO = async (
  url: string,
  method: string,
  body: object,
  ctype: string,
  useToken: boolean,
  useHeaders?: string,
  token?: string,
  abortController?: AbortController,
  credentials?: string,
  isExternalLogin?: boolean,
  pageName?: string
) => {
  let options = {};
  if (method === "GET") {
    options = useHeaders
      ? { method }
      : {
          method,
          headers: requestHeaders(
            ctype,
            useToken,
            token,
            isExternalLogin,
            pageName
          ),
        };
  } else {
    options = {
      method,
      headers: requestHeaders(
        ctype,
        useToken,
        token,
        isExternalLogin,
        pageName
      ),
      body: JSON.stringify(body),
    };
  }
  if (credentials) options = { ...options, credentials };
  const { signal = null } = abortController || {};
  const res = await fetch(url, signal ? { ...options, signal } : options);
  return await parseStatus(res.status, res);
};

const readFromLocalStore = (cacheKey: string) => {
  return JSON.parse(localStorage.getItem(cacheKey));
};

const isCacheDataExpired = (date: string) => {
  return compareAsc(new Date(date), new Date()) === -1 ? true : false;
};

export const fetchCacheUtil = async ({
  url,
  cacheKey,
  ctype,
  useToken,
  token,
  useHeaders,
}: fetchCacheUtilProps) => {
  return new Promise(async (resolve, reject) => {
    const cacheData = readFromLocalStore(cacheKey);
    const cacheDataByURL = cacheData?.[url];
    if (cacheDataByURL && !isCacheDataExpired(cacheDataByURL?.expireDate)) {
      resolve(cacheDataByURL?.data);
    } else {
      let options = {};
      options = useHeaders
        ? { method: "GET" }
        : {
            method: "GET",
            headers: requestHeaders(ctype, useToken, token),
          };
      const res = await fetch(url, options);
      let data = await parseStatus(res.status, res);
      if (
        Array.isArray(data?.data) ||
        Array.isArray(data?.data?.diagnosticWidgetData) ||
        Array.isArray(data?.topItemDetails)
      ) {
        const payload = {
          ...(cacheData ?? {}),
          [url]: {
            data,
            expireDate: addMinutes(new Date(), CACHE_EXPIRE_IN_MINUTES),
          },
        };
        localStorage.setItem(cacheKey, JSON.stringify(payload));
        resolve(data);
      } else {
        reject(data);
      }
    }
  });
};

const parseStatus = (status: any, res: any): any => {
  return new Promise((resolve, reject) => {
    if (status >= 200 && status < 300) {
      if (res.url.indexOf(".html") > -1) {
        return resolve(res);
      }
      res.json().then((response: any) => resolve(response));
    } else {
      res.json().then((response: any) => reject(response));
    }
  });
};

const requestHeaders = (
  ctype: string,
  useToken: boolean,
  token?: string,
  isExternalLogin?: boolean,
  // should silent login only work on the silent login page?
  pageName?: string
) => {
  const externalAuthToken = getUrlParam("t");
  const source = getUrlParam("utm_source");
  let defaultHeader = {
    "Content-Type": ctype || "application/json",
  };
  if (isExternalLogin) {
    if (externalAuthToken) {
      defaultHeader["x-external-auth-token"] = encodeURIComponent(
        externalAuthToken
      );
    }
    if (source) {
      defaultHeader["x-utm-source"] = source;
    }
  }
  return useToken
    ? {
        ...defaultHeader,
        Authorization:
          token || "Basic Y29udGVudDp3YWxtYXJ0TlVUdG9reW9IZWlzdA==",
      }
    : {
        defaultHeader,
      };
};

export default fetchSSO;
